import React, { useState, useEffect, useMemo } from "react";
import { useCalendarStore } from "../stores/calendarStore";
import { useGoogleSyncStore } from "../stores/googleSyncStore";
import { useAuth } from "../hooks/useAuth";
import { useRouter } from "../app/Router";
import { useTheme } from "../hooks/useTheme";
import { MiniMonth } from "./MiniMonth";
import type { CalendarDate } from "../lib/dateUtils";

const COLORS = [
  "#1976d2",
  "#388e3c",
  "#d32f2f",
  "#7b1fa2",
  "#f57c00",
  "#0097a7",
  "#5d4037",
  "#455a64",
];

interface CalendarSidebarProps {
  activeDate: CalendarDate;
  onSelectDate: (date: CalendarDate) => void;
  onNavigateMonth: (delta: number) => void;
}

export function CalendarSidebar({
  activeDate,
  onSelectDate,
  onNavigateMonth,
}: CalendarSidebarProps) {
  const calendars = useCalendarStore((s) => s.calendars);
  const visibility = useCalendarStore((s) => s.visibility);
  const create = useCalendarStore((s) => s.create);
  const remove = useCalendarStore((s) => s.remove);
  const toggleVisibility = useCalendarStore((s) => s.toggleVisibility);
  const isSyncedCalendar = useGoogleSyncStore((s) => s.isSyncedCalendar);
  const getSyncSourceError = useGoogleSyncStore((s) => s.getSyncSourceError);
  // Subscribe to the raw array; derive the failing subset with useMemo so
  // we don't return a fresh array from the zustand selector on every render
  // (which would treat each call as a state change and infinite-loop).
  const syncSources = useGoogleSyncStore((s) => s.syncSources);
  const failingSources = useMemo(
    () => syncSources.filter((src) => src.lastError),
    [syncSources],
  );
  const { logout } = useAuth();
  const { navigate } = useRouter();
  const { theme, cycle } = useTheme();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(COLORS[0]!);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 900px)");
    const handler = (event?: MediaQueryListEvent) => {
      setIsMobile(event ? event.matches : mq.matches);
    };
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    await create({ name: newName.trim(), color: newColor });
    setNewName("");
    setShowCreate(false);
  };

  const themeLabel = theme === "dark" ? "Dark" : theme === "light" ? "Light" : "Auto";

  const sidebarContent = (
    <>
      {/* Mini month navigator */}
      <MiniMonth
        activeDate={activeDate}
        onSelectDate={onSelectDate}
        onNavigateMonth={onNavigateMonth}
      />

      {/* Calendar list */}
      <section className="sidebar-group">
        <header className="sidebar-group-header">
          <span>Calendars</span>
          <button className="sidebar-link" onClick={() => setShowCreate(true)}>
            +
          </button>
        </header>

        <div className="calendar-list">
          {calendars.length === 0 && (
            <p className="muted">No calendars yet.</p>
          )}

          {calendars.map((cal) => {
            const isVisible = visibility[cal.id] !== false;
            const isSynced = isSyncedCalendar(cal.id);
            const syncError = isSynced ? getSyncSourceError(cal.id) : null;
            return (
              <button
                key={cal.id}
                className={`calendar-row ${isVisible ? "active" : ""}`}
                onClick={() => toggleVisibility(cal.id)}
              >
                <span className="calendar-color-dot" style={{ background: cal.color }} />
                <span className="cal-name">
                  {cal.name}
                  {isSynced && !syncError && (
                    <span className="sync-badge" title="Synced from Google">&#x21bb;</span>
                  )}
                  {syncError && (
                    <span
                      className="sync-error-badge"
                      title={`Last sync failed: ${syncError}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/settings");
                      }}
                    >
                      !
                    </span>
                  )}
                </span>
                {!isSynced && (
                  <span
                    className="calendar-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(`Delete "${cal.name}" and all its events?`)) {
                        remove(cal.id);
                      }
                    }}
                  >
                    x
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Inline create form */}
      {showCreate && (
        <section className="sidebar-group">
          <form onSubmit={handleCreate} className="sidebar-create-form">
            <input
              className="input"
              placeholder="Calendar name..."
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
            />
            <div className="color-picker">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`color-swatch ${newColor === c ? "active" : ""}`}
                  style={{ background: c }}
                  onClick={() => setNewColor(c)}
                />
              ))}
            </div>
            <div className="form-actions">
              <button
                className="sidebar-link"
                type="button"
                onClick={() => setShowCreate(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary btn-sm" type="submit">
                Create
              </button>
            </div>
          </form>
        </section>
      )}

      {/* Spacer pushes footer down */}
      <div className="sidebar-spacer" />

      {failingSources.length > 0 && (
        <button
          className="sync-issues-pill"
          onClick={() => navigate("/settings")}
          title={failingSources
            .map((s) => `${s.calendarSummary ?? s.externalCalendarId}: ${s.lastError}`)
            .join("\n")}
        >
          <span className="sync-issues-icon">!</span>
          <span>
            {failingSources.length} sync issue{failingSources.length === 1 ? "" : "s"}
          </span>
        </button>
      )}

      {/* Footer: settings, theme, sign out */}
      <footer className="sidebar-footer">
        <button className="sidebar-footer-link" onClick={() => navigate("/settings")}>
          Settings
        </button>
        <button className="sidebar-footer-link" onClick={cycle}>
          {themeLabel}
        </button>
        <button className="sidebar-footer-link" onClick={logout}>
          Sign out
        </button>
      </footer>
    </>
  );

  if (isMobile) {
    return (
      <div className="mobile-controls">
        <button className="mobile-controls-toggle" onClick={() => setMobileOpen(true)}>
          Menu
        </button>
        {mobileOpen && (
          <div className="mobile-controls-drawer" role="dialog" aria-modal="true">
            <div className="mobile-controls-panel">
              <div className="mobile-controls-header">
                <span>Menu</span>
                <button className="btn btn-ghost btn-sm" onClick={() => setMobileOpen(false)}>
                  Close
                </button>
              </div>
              <div className="mobile-controls-body">
                <div className="calendar-sidebar">{sidebarContent}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return <div className="calendar-sidebar">{sidebarContent}</div>;
}
